import React, { useMemo } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Drawer, DrawerContent, DrawerTitleHeader } from "@/components/ui/drawer";
import cn from "@/utils/classnames";
import Image from 'next/image';
import IconVip from '../images/icon-vip.png';
import Empty from '@/components/ui-feat/empty';
import { Spin } from "@/components/ui-feat/spin";
import { useGullakLogin } from "../hooks/use-gullak-login";
import { useTranslations } from "next-intl";
import { useAtomValue } from "jotai";
import { gullakVipRulesAtom, gullakVipRulesLoadingAtom } from "@/store";
import { isNil } from "lodash-es";
import style from './rule-detail.module.css';
import BgRateCountdown from '../images/bg-rate-countdown.png';
import IconGlod from '../images/icon-gold.png';
import BgPrivilegeRate from '../images/bg-privilege-rate.png';
import StarTitle from './star-title';
import { useCountDown } from "ahooks";
import { useBalanceFormatter } from "@/hooks/use-balance-formatter";
import IconFont from "@/components/ui/iconFont";
import * as Popover from '@radix-ui/react-popover';

export interface RuleDetailProps {
  specialRate?: {
    normalRate: number;
    rate: number;
    countdown: number;  // 单位: 秒
    translate_turnover: number;
    total_turnover: number;
    currency: string;
  };
}

const RuleDetail = NiceModal.create((params: RuleDetailProps) => {
  const { specialRate } = params || {};
  const modal = useModal();
  const vipRulesData = useAtomValue(gullakVipRulesAtom);
  const isLoading = useAtomValue(gullakVipRulesLoadingAtom);
  const { isLogin } = useGullakLogin();
  const t_gullak = useTranslations('gullak');
  const [countdown, formattedRes] = useCountDown({ leftTime: (specialRate?.countdown || 0) * 1000 });
  const { formatCurrency } = useBalanceFormatter();

  const turnoverTipText = t_gullak('specialRateRule', {
    amount: formatCurrency(specialRate?.total_turnover || 0, specialRate?.currency || 'INR')
  });

  const handleClose = () => {
    modal.hide();
  };

  // 转换 API 数据格式
  const vipRateList = vipRulesData?.vip_gullak_rate || [];
  const currentVipLevel = vipRulesData?.vip_level;
  const isEmpty = vipRateList.length === 0;

  // 限时转化加成
  const renderCountdown = () => {
    if (!countdown) return null;
    const timeArr = [
      formattedRes.days ? formattedRes.days + 'd' : '',
      formattedRes.hours.toString().padStart(2, '0'),
      formattedRes.minutes.toString().padStart(2, '0'),
      formattedRes.seconds.toString().padStart(2, '0'),
    ].filter((str) => !!str);
    const len = timeArr.length;
    return (
      <div className="flex items-center justify-center text-white font-medium text-[3.1vw]">
        {timeArr.map((item, i) => (
          <>
            <div key={i} className={`${style['countdown-box']} h-[23px] px-1 flex items-center justify-center`}>
              {item}
            </div>
            {i !== len - 1 && <div className="w-[2.6vw] h-[3.1vw] px-1 flex items-center justify-center">:</div>}
          </>
        ))}
      </div>
    );
  };

  // 动态计算 drawer 高度
  // gullakRulesDescription: 48px，每行内容: 44px，最大高度: 72vh
  const drawerHeight = useMemo(() => {
    if (isEmpty || isLoading) {
      // 空状态或加载中：基础高度（标题 + 描述 + 空状态内容）
      return '50vh';
    }

    // 计算内容高度：
    // - 标题栏（DrawerTitleHeader）：约 60px
    // - padding: 16px * 2 = 32px
    // - gullakRulesDescription: 大概64px，按最小机型去适配，显示成4行的情况
    // - 表头 + 分割线: 44px
    // - 每行内容: 44px
    // - 间距: 16px (mt-4)
    const headerHeight = 60;
    const marginHeight = 16;
    const paddingHeight = 16;
    const descriptionHeight = 64;
    const tableHeaderHeight = 30;
    const spacingHeight = 16;
    const rowHeight = 44;

    const contentHeight =
      headerHeight +
      marginHeight * 2 +
      paddingHeight * 2 +
      descriptionHeight +
      spacingHeight +
      tableHeaderHeight +
      (vipRateList.length * rowHeight);

    // 将 px 转换为 vh
    const heightInVh = (contentHeight / window.innerHeight) * 100;

    // 限制在 72vh 以内
    const finalHeight = Math.min(heightInVh, 72);

    return `${finalHeight}vh`;
  }, [vipRateList.length, isEmpty, isLoading]);

  return (
    <Drawer open={modal.visible} onOpenChange={handleClose}>
      <DrawerContent className="bg-[#181D29] flex" style={{ height: drawerHeight }}>
        <DrawerTitleHeader title={t_gullak('rules')} showClose />
        <div className="h-full overflow-auto no-scrollbar px-4 flex flex-col">
          <div className="text-[#586272] font-medium text-[13px] leading-4 flex items-center">
            {t_gullak('gullakRulesDescription')}
          </div>
          {!isNil(specialRate) && !!countdown && (
            <div className="font-medium bg-[#131720] rounded-lg mt-4 py-[3.1vw] px-[3.9vw]">
              {/* gullak rate */}
              <StarTitle text={t_gullak('gullakRateTitle')} className="mb-[2.6vw] mx-auto w-fit" textClassName="!text-[3.1vw]" />
              <div className="relative w-full">
                <Image src={BgRateCountdown.src} width={330} height={32.5} style={{ width: '100%', aspectRatio: '660/75' }} alt="rate" />
                <div className="absolute top-[50%] -translate-y-1/2 flex items-center">
                  <div className="ml-[4.68vw] flex items-center">
                    <Image src={IconGlod.src} width={20} height={16} alt="gold" style={{ width: '3.64vw', aspectRatio: '80/64' }} />
                    <div className={`${style['pre-rate']} text-[2.86vw] font-bold ml-1`}>{specialRate.normalRate || 0}&nbsp;%</div>
                  </div>
                  <div className="absolute left-[24.7vw] flex items-center">
                    <div
                      className="relative flex items-center px-2 py-1 bg-no-repeat whitespace-nowrap"
                      style={{ backgroundImage: `url(${BgPrivilegeRate.src})`, backgroundSize: '100% 100%' }}
                    >
                      <Image
                        src={IconGlod.src}
                        width={28}
                        height={22.4}
                        alt="gold"
                        style={{ width: '4.4vw', height: 'auto', aspectRatio: '80/64' }}
                      />
                      <div className={`${style['temp-rate']} text-[3.9vw] font-bold leading-[4.8vw] mr-[6vw]`}>
                        {specialRate.rate || 0}%
                      </div>
                    </div>
                    <div className="ml-3">{renderCountdown()}</div>
                  </div>
                </div>
              </div>
              <Popover.Root>
                <Popover.Trigger asChild>
                  <div className="mt-[2.3vw] flex items-center">
                    <div className="text-tertiary font-semibold text-[2.6vw] h-3 leading-3 whitespace-nowrap">
                      {t_gullak('dailyTurnover')}:
                      <span className="text-secondary ml-2">{formatCurrency(specialRate?.translate_turnover || 0, specialRate?.currency)}</span>
                      <span> / </span>
                      <span>{formatCurrency(specialRate?.total_turnover || 0, specialRate?.currency)}</span>
                    </div>
                    <IconFont name="icon-tips" size={10} color="#748094" className="ml-1" />
                  </div>
                </Popover.Trigger>
                <Popover.Content
                  side="bottom"
                  align="center"
                  sideOffset={8}
                  className="z-[1000] max-w-[72.5vw] rounded-lg bg-[#455065] p-[2.6vw] text-[2.6vw] text-secondary"
                >
                  {turnoverTipText}
                </Popover.Content>
              </Popover.Root>
            </div>
          )}
          <div className="my-4 py-4 text-ss font-medium bg-[#131720] rounded-lg overflow-hidden overflow-y-auto no-scrollbar">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 h-full">
                <Spin />
              </div>
            ) : isEmpty ? (
              <Empty className="py-8 h-full" />
            ) : (
              <>
                <div className="px-4 flex items-center justify-between mb-3 text-[#748094]">
                  <div className="flex items-center">
                    <Image src={IconVip.src} width={20} height={20} alt="vip" />
                    <span>{t_gullak('vip')}</span>
                  </div>
                  <div>{t_gullak('gullakRateTitle')}</div>
                </div>
                <div className="mx-4 h-[1px] bg-[rgba(214,234,255,0.06)]" />
                <div className="overflow-hidden overflow-y-auto no-scrollbar">
                  {vipRateList.map((item, index) => {
                    const isSelf = isLogin ? item.vip_level === currentVipLevel : false;
                    return (
                      <React.Fragment key={index}>
                        <div className={cn("text-[#A9B5CB] font-[Montserrat]", { 'bg-[rgba(0,89,255,0.06)] text-white': isSelf })}>
                          <div className="px-4 flex items-center justify-between h-[44px]">
                            <div>VIP&nbsp;&nbsp;{item.vip_level}{isSelf && `（${t_gullak('you')}）`}</div>
                            <div>{item.vip_rate}%</div>
                          </div>
                        </div>
                        <div className="mx-4 h-[1px] bg-[rgba(214,234,255,0.06)]" />
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
});

export default RuleDetail;
